import { ProposalStatus } from '@/types'
import React from 'react'
import { ActionLink } from '../../../ui/ActionLink'
import { formatCurrency } from '../../../../utils/currency'

interface StatusButtonProps {
  status: ProposalStatus
  className?: string
}

const Status: React.FC<StatusButtonProps> = ({ status, className = '' }) => {
  const getStatusConfig = (status: ProposalStatus) => {
    switch (status) {
      case 'interesada':
        return {
          text: 'Interesada',
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          icon: '●',
          textSize: 'text-xs',
        }
      case 'En espera':
        return {
          text: 'En espera',
          bgColor: 'bg-[#EBC011]',
          textColor: 'text-white',
          icon: '●',
          textSize: 'text-xs',
        }
      case 'Seleccionada':
        return {
          text: 'Seleccionada',
          bgColor: 'bg-yellow-500',
          textColor: 'text-white',
          icon: '●',
          textSize: 'text-xs',
        }
      default:
        return {
          text: status,
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          icon: '●',
          textSize: 'text-xs',
        }
    }
  }

  const config = getStatusConfig(status)

  return (
    <div
      className={`
        py-1 px-2 sm:py-2 rounded-md font-medium ${config.textSize}
        flex items-center justify-center gap-1 sm:gap-2
        ${config.bgColor} ${config.textColor}
        w-full min-w-[90px] max-w-[180px]
        ${className}
      `}
    >
      <span className="text-xs">{config.icon}</span>
      {config.text}
    </div>
  )
}

interface PendingProposalItemProps {
  id: string
  marca: string
  modelo: string
  valorFactura: string
  status: ProposalStatus
  created_at: string
  startTime?: string
  // ya generó una credit application en el pasado — muestra el cintillo
  // "Ya solicitada" y no ofrece ninguna acción.
  disabled?: boolean
  // es la cotización con una solicitud EN CURSO (la credit application
  // activa del usuario, sin llegar a un estatus terminal). Muestra
  // "Continuar" en vez de "Solicitar" y retoma el progressUrl guardado.
  inProgress?: boolean
  onResume?: () => void
  // presente = la tarjeta puede solicitarse; dispara la creación de la
  // credit application. Si se omite (vencida, o bloqueada porque ya hay
  // otra solicitud activa) no se muestra ninguna acción.
  onRequest?: () => void
  // true mientras esta tarjeta específica está creando su credit
  // application (ver requestingProposalId en PendingProposalsContent) —
  // deshabilita el botón "Solicitar" y muestra que está procesando.
  isRequesting?: boolean
}

const PendingProposalItem: React.FC<PendingProposalItemProps> = ({
  marca,
  modelo,
  valorFactura,
  status,
  created_at,
  startTime,
  disabled = false,
  inProgress = false,
  onResume,
  onRequest,
  isRequesting = false,
}) => {
  const action = inProgress ? (
    <ActionLink label="Continuar" onClick={() => onResume?.()} />
  ) : !disabled && (onRequest || isRequesting) ? (
    <ActionLink label="Solicitar" onClick={onRequest} isLoading={isRequesting} />
  ) : null

  return (
    <div className="relative w-full bg-white rounded-lg shadow-md border border-gray-200 p-3 sm:px-6 sm:py-4 mb-4 text-left">
      {/* Cintillo superpuesto: no ocupa espacio en el flujo, así todas las
          tarjetas miden igual tengan o no esta leyenda. */}
      {disabled && !inProgress && (
        <span className="pointer-events-none absolute -top-2 left-3 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-md px-2 py-1 shadow-sm">
          Ya solicitada
        </span>
      )}
      {/* Mobile Layout */}
      <div className="block sm:hidden">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cotización {marca}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-900">
                {modelo}
              </span>
            </div>
          </div>

          <div className="flex-shrink-0">
            <Status status={status} />
          </div>
        </div>

        {/* Mobile Details */}
        <div className="ml-0 space-y-2 mb-3">
          <div className="flex justify-between">
            <span className="text-xs text-gray-500">Valor factura:</span>
            <span className="text-xs font-medium text-gray-900">
              {formatCurrency(valorFactura)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-gray-500">Fecha:</span>
            <span className="text-xs font-medium text-gray-900">
              {created_at}
            </span>
          </div>
        </div>

        {action && <div className="flex justify-end">{action}</div>}
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div>
          {/* Título y Status+acción en la misma línea — el botón vive
              siempre debajo del status, en la misma columna de 150px, así
              su presencia o el largo de su texto nunca desalinean nada. */}
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xl font-semibold text-gray-900">
              Cotización {marca}
            </h3>
            <div className="w-[150px] flex flex-col items-end gap-1">
              <Status status={status} />
              {action}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-2">
              <p className="text-sm text-gray-500 mb-1">Modelo</p>
              <p className="text-sm font-medium text-gray-900 truncate">
                {modelo}
              </p>
            </div>

            <div className="col-span-3">
              <p className="text-sm text-gray-500 mb-1">Valor factura</p>
              <p className="text-sm font-medium text-gray-900">
                {formatCurrency(valorFactura)}
              </p>
            </div>

            <div className="col-span-3">
              <p className="text-sm text-gray-500 mb-1">Fecha de creación</p>
              <p className="text-sm font-medium text-gray-900">{created_at}</p>
            </div>

            <div className="col-span-4">
              {startTime && (
                <>
                  <p className="text-sm text-gray-500 mb-1">Fecha de visita</p>
                  <p className="text-sm font-medium text-gray-900">
                    {startTime}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PendingProposalItem
export { Status }
